import React from 'react';
import moment from 'moment';
import { formatAmount } from '../../../utils/money-util';
import { formatPhoneNumber } from '../../../utils/number-util';
import { getClaimEmailTemplate, getClaimEmailTemplates } from '../../../core/api/reclamations';
import { hasRole, RoleTypes } from '../../../core/components/Permission';
import { ReclamationType } from '../models';
import ContentTabs from '../../../core/components/ContentTabs';
import { Files } from '../../../core/components/Files';
import Photos from '../../../core/components/Photos';
import Videos from '../../../core/components/Videos';
import Repair from '../../claimDetail/components/Repair';
import ClaimEmailPanel from './ClaimEmailPanel';
import ClaimCallLogPanel from './ClaimCallLogPanel';
import ClaimFollowUpPanel from './ClaimFollowUpPanel';
import { INVOLVED_OPTIONS, RELATIONSHIP_OPTIONS } from './claimWizard/claimWizardConstants';
import {
  fallbackEmailTemplates,
  DEFAULT_EMAIL_TEMPLATE_KEY,
  filterClaimEmailTemplates,
  getClaimEmailTemplateIdentifier,
  getClaimEmailTemplatesFromResponse,
  resolveDefaultEmailTemplateKey,
  getRenderedEmailTemplateResponse,
  TIMELINE_LABELS,
  formatTimelineCreator,
  sortTimelineItems,
  getVisibleTimelineItems,
  getTimelineHistoryGroups,
  getPrimaryRecipient,
  buildOperatorGroupPayload,
  getVenuePayload,
  getSendEmailSiteId,
  getDialNumber,
  buildEmailTemplateRenderKey,
  formatCallOutcome,
  getTimelineNotePreview,
  shouldShowTimelineNoteToggle,
  refreshReclamationRecord,
  getRecentActiveNotes,
  getTimelineTitle,
} from '../utils/claimDrawerHelpers';

const CLAIM_EVENT_LABELS = {
  collision: 'Collision',
  equipment_damage: 'Equipment Damage',
  car_damage_from_equipment: 'Damage from Equipment',
  other: 'Other',
};

const VEHICLE_DETAIL_FIELDS = [
  { key: 'make', label: 'Make' },
  { key: 'model', label: 'Model' },
  { key: 'year', label: 'Year' },
  { key: 'color', label: 'Color' },
  { key: 'vin', label: 'VIN' },
  { key: 'plate', label: 'License Plate' },
  { key: 'membershipNumber', label: 'Fleet/Membership Number' },
];

const formatYesNo = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return null;
};

const getEmployeeId = (employeeAssociation) =>
  employeeAssociation?.employeeId ??
  employeeAssociation?.employee?.id ??
  employeeAssociation?.id ??
  employeeAssociation;

const getEmbeddedEmployee = (employeeAssociation) =>
  employeeAssociation?.employee ||
  (employeeAssociation?.firstName || employeeAssociation?.lastName ? employeeAssociation : null);

const ClaimDrawerContent = ({
  reclamation,
  employees,
  damageClaimOptions,
  addFile,
  addReclamationNote,
  deleteFile,
  showDialog,
  patchReclamation,
  sendReclamationEmail,
  getReclamation,
  operatorGroup,
  role,
  currentUser,
  venue,
  intl,
  scrollToMediaSection,
  onMediaSectionFocused,
  initialEmailTemplate,
  initialEstimatesCount,
  initialDesignatedRepairShop,
  initialCustomerAction,
  hideCustomerActions,
}) => {
  const [activeCustomerAction, setActiveCustomerAction] = React.useState(null);
  const [followUpDate, setFollowUpDate] = React.useState(null);
  const [followUpNotes, setFollowUpNotes] = React.useState('');
  const [callNotes, setCallNotes] = React.useState('');
  const [callOutcome, setCallOutcome] = React.useState('connected');
  const [smsMessage, setSmsMessage] = React.useState('');
  const [emailTemplates, setEmailTemplates] = React.useState([]);
  const [emailTemplate, setEmailTemplate] = React.useState('');
  const [emailBody, setEmailBody] = React.useState('');
  const [emailTemplateResponse, setEmailTemplateResponse] = React.useState(null);
  const [loadedEmailSubject, setLoadedEmailSubject] = React.useState('');
  const [loadedEmailHtml, setLoadedEmailHtml] = React.useState('');
  const [isLoadingEmailTemplates, setIsLoadingEmailTemplates] = React.useState(false);
  const [isLoadingEmailBody, setIsLoadingEmailBody] = React.useState(false);
  const [emailRecipients, setEmailRecipients] = React.useState(
    reclamation.customerEmail ? [reclamation.customerEmail] : []
  );
  const [emailInput, setEmailInput] = React.useState('');
  const [showAllTimeline, setShowAllTimeline] = React.useState(false);
  const canScheduleFollowUp = !!followUpDate;
  const [expandedTimelineNotes, setExpandedTimelineNotes] = React.useState({});
  const [showEmailHistory, setShowEmailHistory] = React.useState(false);
  const [showSmsHistory, setShowSmsHistory] = React.useState(false);
  const [showCallHistory, setShowCallHistory] = React.useState(false);
  const [showFollowUpHistory, setShowFollowUpHistory] = React.useState(false);
  const refreshReclamation = () => refreshReclamationRecord(getReclamation, reclamation?.id);
  const canCommunicate = hasRole(role, RoleTypes.SiteManager);

  const canSendSms = false;
  const primaryRecipient = getPrimaryRecipient(emailRecipients);
  const operatorGroupPayload = buildOperatorGroupPayload(operatorGroup);
  const venuePayload = getVenuePayload(venue, reclamation);
  const isClaimEmailOptedOut = currentUser?.emailOptOut === true;
  const canSendEmail = canCommunicate && !isClaimEmailOptedOut;
  const canLogCall = canCommunicate;
  const sendEmailSiteId = getSendEmailSiteId(reclamation);
  const dialNumber = getDialNumber(reclamation.customerPhone);
  const timelineItems = sortTimelineItems(reclamation.history);
  const visibleTimelineItems = getVisibleTimelineItems(timelineItems, showAllTimeline, 5);
  const { emailHistoryItems, smsHistoryItems, callHistoryItems, followUpHistoryItems } =
    getTimelineHistoryGroups(timelineItems);
  const timelineNoteMax = 140;
  const formatStatusName = (statusName) =>
    intl?.formatMessage ? intl.formatMessage({ id: statusName }) : statusName;
  const emailTemplateRenderKey = buildEmailTemplateRenderKey(reclamation, venuePayload);
  const recentActiveNotes = getRecentActiveNotes(reclamation.notes);
  const claimEmployeeAssociations = reclamation.employeeIds || reclamation.employees || [];
  const primaryEmployeeAssociation = claimEmployeeAssociations[0];
  const claimEmployeeId = primaryEmployeeAssociation
    ? getEmployeeId(primaryEmployeeAssociation)
    : null;
  const claimEmployee = primaryEmployeeAssociation
    ? employees?.find((employee) => String(employee.id) === String(claimEmployeeId)) ||
      getEmbeddedEmployee(primaryEmployeeAssociation)
    : null;
  const claimEmployeeName = claimEmployee
    ? `${claimEmployee.firstName} ${claimEmployee.lastName}`.trim()
    : '';
  const vehicleDetails = reclamation.vehicleDetails || {};
  const visibleVehicleDetails = VEHICLE_DETAIL_FIELDS.filter(({ key }) => vehicleDetails[key]);
  const mediaSectionRef = React.useRef(null);
  const emailSectionRef = React.useRef(null);
  const claimChecklist = reclamation.incidentDetails?.claimChecklist || {};
  const claimEventType = claimChecklist.eventType;
  const claimEventLabel = CLAIM_EVENT_LABELS[claimEventType] || claimEventType;
  const involvedOption = INVOLVED_OPTIONS.find(
    (option) => option.value === claimChecklist.involved
  );
  const involvedLabel = involvedOption
    ? intl.formatMessage({ id: involvedOption.labelId })
    : claimChecklist.involved;
  const relationshipOption = RELATIONSHIP_OPTIONS.find(
    (option) => option.value === claimChecklist.primaryContactRelationship
  );
  const relationshipLabel = relationshipOption
    ? intl.formatMessage({ id: relationshipOption.labelId })
    : claimChecklist.primaryContactRelationship;
  const additionalContact = claimChecklist.additionalContact || {};
  const shouldShowClaimChecklist =
    reclamation.type === ReclamationType.Claim &&
    (claimChecklist.involved ||
      claimChecklist.eventDate ||
      claimChecklist.eventTime ||
      claimChecklist.primaryContactRelationship ||
      claimChecklist.anotherPartyInvolved !== undefined ||
      claimChecklist.policeReportFiled !== undefined ||
      claimChecklist.injuries !== undefined ||
      additionalContact.name ||
      additionalContact.contact);
  const shouldShowClaimDamageInfo =
    reclamation.type === ReclamationType.Claim &&
    (claimEventType ||
      reclamation.incidentDetails?.damageInformation ||
      reclamation.claimDamagePartId ||
      reclamation.claimDamageAreaId);

  React.useEffect(() => {
    if (!initialEmailTemplate || !canSendEmail) return;
    setEmailTemplate(initialEmailTemplate);
    setActiveCustomerAction('email');
    const timeout = setTimeout(() => {
      emailSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 250);
    return () => clearTimeout(timeout);
  }, [canSendEmail, initialEmailTemplate]);

  React.useEffect(() => {
    if (initialCustomerAction !== 'call' || !canLogCall) return;
    setActiveCustomerAction('call');
    const timeout = setTimeout(() => {
      emailSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 250);
    return () => clearTimeout(timeout);
  }, [canLogCall, initialCustomerAction]);

  React.useEffect(() => {
    if (!scrollToMediaSection) return;

    const timeout = setTimeout(() => {
      if (mediaSectionRef.current) {
        mediaSectionRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
      if (onMediaSectionFocused) {
        onMediaSectionFocused();
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [onMediaSectionFocused, reclamation.id, scrollToMediaSection]);

  React.useEffect(() => {
    let isMounted = true;

    const loadEmailTemplates = async () => {
      if (activeCustomerAction !== 'email') return;
      setIsLoadingEmailTemplates(true);
      try {
        const response = await getClaimEmailTemplates({
          data: {
            query: {
              pageSize: 0,
            },
          },
        });
        if (!isMounted) return;
        const fetchedTemplates = getClaimEmailTemplatesFromResponse(response);
        const nextTemplates = filterClaimEmailTemplates(fetchedTemplates);
        setEmailTemplates(nextTemplates);
        setEmailTemplate((currentTemplate) =>
          resolveDefaultEmailTemplateKey(nextTemplates, currentTemplate)
        );
      } catch (error) {
        if (!isMounted) return;
        setEmailTemplates(fallbackEmailTemplates);
        setEmailTemplate(DEFAULT_EMAIL_TEMPLATE_KEY);
      } finally {
        if (isMounted) {
          setIsLoadingEmailTemplates(false);
        }
      }
    };

    loadEmailTemplates();

    return () => {
      isMounted = false;
    };
  }, [activeCustomerAction]);

  React.useEffect(() => {
    let isMounted = true;

    const loadEmailBody = async () => {
      if (!emailTemplate) {
        setEmailTemplateResponse(null);
        setEmailBody('');
        setLoadedEmailSubject('');
        return;
      }

      setIsLoadingEmailBody(true);
      try {
        const response = await getClaimEmailTemplate({
          data: { templateKey: emailTemplate },
        });
        if (!isMounted) return;
        setEmailTemplateResponse(response);
        const {
          loadedEmailSubject: nextLoadedEmailSubject,
          loadedEmailHtml: nextLoadedEmailHtml,
          emailBody: nextEmailBody,
        } = getRenderedEmailTemplateResponse(response, emailTemplate, {
          reclamation,
          venue: venuePayload,
          operatorGroup: operatorGroupPayload,
        });
        setLoadedEmailSubject(nextLoadedEmailSubject);
        setLoadedEmailHtml(nextLoadedEmailHtml);
        setEmailBody(nextEmailBody);
      } catch (error) {
        if (!isMounted) return;
        setEmailTemplateResponse(null);
        const {
          loadedEmailSubject: nextLoadedEmailSubject,
          loadedEmailHtml: nextLoadedEmailHtml,
          emailBody: nextEmailBody,
        } = getRenderedEmailTemplateResponse(null, emailTemplate, {
          reclamation,
          venue: venuePayload,
          operatorGroup: operatorGroupPayload,
        });
        setLoadedEmailSubject(nextLoadedEmailSubject);
        setLoadedEmailHtml(nextLoadedEmailHtml);
        setEmailBody(nextEmailBody);
      } finally {
        if (isMounted) {
          setIsLoadingEmailBody(false);
        }
      }
    };

    loadEmailBody();

    return () => {
      isMounted = false;
    };
  }, [emailTemplate, emailTemplateRenderKey, operatorGroupPayload?.name]);

  React.useEffect(() => {
    if (activeCustomerAction === 'email' && !canSendEmail) {
      setActiveCustomerAction(null);
    }
    if (activeCustomerAction === 'call' && !canLogCall) {
      setActiveCustomerAction(null);
    }
  }, [activeCustomerAction, canSendEmail, canLogCall]);

  return (
    <div>
      {/* Employees */}
      {claimEmployeeAssociations.length > 0 && (
        <div className="drawer-section">
          <h4 className="drawer-section-title">Employees</h4>
          <div className="drawer-list">
            {claimEmployeeAssociations.map((emp, idx) => {
              const employeeId = getEmployeeId(emp);
              const employee =
                employees?.find((e) => String(e.id) === String(employeeId)) ||
                getEmbeddedEmployee(emp);
              return (
                <div key={employeeId || idx} className="drawer-list-item">
                  <strong>Employee #{idx + 1}:</strong>{' '}
                  {employee ? `${employee.firstName} ${employee.lastName}`.trim() : employeeId}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Customer Details */}
      {(reclamation.customerName ||
        reclamation.customerEmail ||
        reclamation.customerPhone ||
        relationshipLabel) && (
        <div className="drawer-section">
          <h4 className="drawer-section-title">Customer Details</h4>
          <div className="drawer-grid">
            {reclamation.customerName && (
              <div className="drawer-field">
                <strong>Name:</strong> {reclamation.customerName}
              </div>
            )}
            {reclamation.customerEmail && (
              <div className="drawer-field">
                <strong>Email:</strong> {reclamation.customerEmail}
              </div>
            )}
            {reclamation.customerPhone && (
              <div className="drawer-field">
                <strong>Phone:</strong> {formatPhoneNumber(reclamation.customerPhone)}
              </div>
            )}
            {relationshipLabel && (
              <div className="drawer-field">
                <strong>Relationship to Claim:</strong> {relationshipLabel}
              </div>
            )}
          </div>
        </div>
      )}

      {!hideCustomerActions &&
        (reclamation.customerName || reclamation.customerEmail || reclamation.customerPhone) && (
          <div className="drawer-section" ref={emailSectionRef}>
            <h4 className="drawer-section-title">Customer Actions</h4>
            <div className="drawer-actions-grid">
              <button
                type="button"
                className={`drawer-action-button${
                  activeCustomerAction === 'email' ? ' is-active' : ''
                }${!canSendEmail ? ' is-disabled' : ''}`}
                onClick={() => {
                  if (!canSendEmail) return;
                  setActiveCustomerAction((prev) => (prev === 'email' ? null : 'email'));
                }}
                disabled={!canSendEmail}
                aria-disabled={!canSendEmail}
                title={
                  isClaimEmailOptedOut
                    ? 'Claim emails are disabled for your user.'
                    : !canSendEmail
                    ? 'Manager permissions required'
                    : undefined
                }
              >
                <i className="uil uil-at" />
                <span>Email</span>
              </button>
              {/*
            <button
              type="button"
              className={`drawer-action-button${
                activeCustomerAction === 'sms' ? ' is-active' : ''
              }`}
              onClick={() => {
                if (!canSendSms) return;
                if (!canCommunicate) return;
                setActiveCustomerAction((prev) => (prev === 'sms' ? null : 'sms'));
              }}
              disabled={!canCommunicate || !canSendSms}
              title={
                !canCommunicate
                  ? 'Manager permissions required'
                  : !canSendSms
                  ? 'SMS disabled'
                  : undefined
              }
            >
              <i className="uil uil-comment" />
              <span>SMS</span>
            </button>
            */}
              <button
                type="button"
                className={`drawer-action-button${
                  activeCustomerAction === 'call' ? ' is-active' : ''
                }${!canLogCall ? ' is-disabled' : ''}`}
                onClick={() => {
                  if (!canLogCall) return;
                  setActiveCustomerAction((prev) => (prev === 'call' ? null : 'call'));
                }}
                disabled={!canLogCall}
                aria-disabled={!canLogCall}
                title={!canLogCall ? 'Manager permissions required' : undefined}
              >
                <i className="uil uil-phone" />
                <span>Call</span>
              </button>
              <button
                type="button"
                className={`drawer-action-button${
                  activeCustomerAction === 'note' ? ' is-active' : ''
                }`}
                onClick={() => {
                  setActiveCustomerAction('note');
                  if (!addReclamationNote) return;
                  showDialog('ADD_NOTE', {
                    title: intl.formatMessage({ id: 'addNote' }),
                    onReady: (values) =>
                      addReclamationNote({
                        ...values,
                        reclamationId: reclamation.id,
                      }),
                  });
                }}
              >
                <i className="uil uil-edit" />
                <span>Note</span>
              </button>
              <button
                type="button"
                className={`drawer-action-button${
                  activeCustomerAction === 'follow-up' ? ' is-active' : ''
                }`}
                onClick={() =>
                  setActiveCustomerAction((prev) => (prev === 'follow-up' ? null : 'follow-up'))
                }
              >
                <i className="uil uil-calendar-alt" />
                <span>Follow-up</span>
              </button>
            </div>
            {!canCommunicate && (
              <div className="drawer-actions-permission-note">
                Manager permissions required for email and call actions.
              </div>
            )}
            {activeCustomerAction === 'sms' && canSendSms && (
              <div className="drawer-sms">
                <div className="drawer-sms-header">
                  <div className="drawer-sms-title">Send Text Message</div>
                  {reclamation.customerPhone && (
                    <div className="drawer-sms-to">
                      To {formatPhoneNumber(reclamation.customerPhone)}
                    </div>
                  )}
                </div>
                <textarea
                  className="form-control drawer-sms-input"
                  rows={4}
                  placeholder="Type your message..."
                  value={smsMessage}
                  onChange={(event) => setSmsMessage(event.target.value)}
                />
                <div className="drawer-sms-actions">
                  <button
                    type="button"
                    className="button inverted"
                    onClick={() => {
                      setActiveCustomerAction(null);
                      setSmsMessage('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="button primary"
                    disabled={!smsMessage.trim() || !canCommunicate}
                    onClick={() => {
                      if (!smsMessage.trim() || !patchReclamation || !canCommunicate) return;
                      patchReclamation({
                        reclamation: {
                          id: reclamation.id,
                          activityType: 'sms_sent',
                          activityDetails: {
                            sentAt: new Date().toISOString(),
                            message: smsMessage.trim(),
                          },
                        },
                        callback: () => {
                          refreshReclamation();
                          setActiveCustomerAction(null);
                          setSmsMessage('');
                        },
                      });
                    }}
                  >
                    Send Text
                  </button>
                </div>
              </div>
            )}
            {activeCustomerAction === 'sms' && canSendSms && (
              <div className="drawer-action-history">
                <button
                  type="button"
                  className="button inverted drawer-action-history-toggle"
                  onClick={() => setShowSmsHistory((prev) => !prev)}
                >
                  {showSmsHistory ? 'Hide Text History' : 'Show Text History'}
                </button>
                {showSmsHistory &&
                  (smsHistoryItems.length > 0 ? (
                    <div className="drawer-action-history-list">
                      {smsHistoryItems.slice(0, 5).map((item, index) => (
                        <div key={item.id || item.createdAt || index} className="drawer-list-item">
                          <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                            {item.createdAt
                              ? moment(item.createdAt).format('MMM D, YYYY h:mm A')
                              : ''}
                          </div>
                          <div className="drawer-action-history-detail">
                            {item.details?.message || 'Text sent'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="drawer-action-history-empty">No text history yet.</div>
                  ))}
              </div>
            )}
            {activeCustomerAction === 'email' && canSendEmail && (
              <ClaimEmailPanel
                reclamation={reclamation}
                canSendEmail={canSendEmail}
                isClaimEmailOptedOut={isClaimEmailOptedOut}
                emailRecipients={emailRecipients}
                setEmailRecipients={setEmailRecipients}
                emailInput={emailInput}
                setEmailInput={setEmailInput}
                emailTemplate={emailTemplate}
                setEmailTemplate={setEmailTemplate}
                initialEstimatesCount={initialEstimatesCount}
                initialDesignatedRepairShop={initialDesignatedRepairShop}
                emailTemplates={emailTemplates}
                isLoadingEmailTemplates={isLoadingEmailTemplates}
                isLoadingEmailBody={isLoadingEmailBody}
                loadedEmailSubject={loadedEmailSubject}
                emailBody={emailBody}
                loadedEmailHtml={loadedEmailHtml}
                emailTemplateResponse={emailTemplateResponse}
                setActiveCustomerAction={setActiveCustomerAction}
                primaryRecipient={primaryRecipient}
                sendEmailSiteId={sendEmailSiteId}
                operatorGroupPayload={operatorGroupPayload}
                venuePayload={venuePayload}
                refreshReclamation={refreshReclamation}
                sendReclamationEmail={sendReclamationEmail}
                addFile={(formData) => {
                  addFile({
                    siteId: reclamation.siteId,
                    reclamationId: reclamation.id,
                    file: formData,
                  });
                }}
                showEmailHistory={showEmailHistory}
                setShowEmailHistory={setShowEmailHistory}
                emailHistoryItems={emailHistoryItems}
              />
            )}
            {activeCustomerAction === 'call' && canLogCall && (
              <ClaimCallLogPanel
                reclamation={reclamation}
                dialNumber={dialNumber}
                callNotes={callNotes}
                setCallNotes={setCallNotes}
                callOutcome={callOutcome}
                setCallOutcome={setCallOutcome}
                canLogCall={canLogCall}
                patchReclamation={patchReclamation}
                refreshReclamation={refreshReclamation}
                setActiveCustomerAction={setActiveCustomerAction}
                showCallHistory={showCallHistory}
                setShowCallHistory={setShowCallHistory}
                callHistoryItems={callHistoryItems}
                expandedTimelineNotes={expandedTimelineNotes}
                setExpandedTimelineNotes={setExpandedTimelineNotes}
                timelineNoteMax={timelineNoteMax}
              />
            )}
            {activeCustomerAction === 'follow-up' && (
              <ClaimFollowUpPanel
                reclamation={reclamation}
                followUpDate={followUpDate}
                setFollowUpDate={setFollowUpDate}
                followUpNotes={followUpNotes}
                setFollowUpNotes={setFollowUpNotes}
                canScheduleFollowUp={canScheduleFollowUp}
                patchReclamation={patchReclamation}
                refreshReclamation={refreshReclamation}
                setActiveCustomerAction={setActiveCustomerAction}
                showFollowUpHistory={showFollowUpHistory}
                setShowFollowUpHistory={setShowFollowUpHistory}
                followUpHistoryItems={followUpHistoryItems}
              />
            )}
          </div>
        )}

      {/* Vehicle Details */}
      {visibleVehicleDetails.length > 0 && (
        <div className="drawer-section">
          <h4 className="drawer-section-title">Vehicle Details</h4>
          <div className="drawer-grid">
            {visibleVehicleDetails.map(({ key, label }) => (
              <div className="drawer-field" key={key}>
                <strong>{label}:</strong> {vehicleDetails[key]}
              </div>
            ))}
          </div>
        </div>
      )}

      {reclamation.cost !== undefined && reclamation.cost !== null && (
        <div className="drawer-section">
          <h4 className="drawer-section-title">Repair Cost</h4>
          <div className="drawer-field">{formatAmount(reclamation.cost)}</div>
        </div>
      )}

      {recentActiveNotes.length > 0 && (
        <div className="drawer-section">
          <h4 className="drawer-section-title">Recent Notes</h4>
          <div className="drawer-list">
            {recentActiveNotes.map((note) => (
              <div key={note.id || note.createdAt} className="drawer-list-item">
                <div className="drawer-note-meta">
                  <span>{note.createdAt ? moment(note.createdAt).format('MMM D, YYYY') : ''}</span>
                  {note.creator && <span>{note.creator}</span>}
                </div>
                <div>{note.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {timelineItems.length > 0 && (
        <div className="drawer-section">
          <h4 className="drawer-section-title">Timeline</h4>
          <div className="drawer-timeline">
            {visibleTimelineItems.map((item, index) => {
              const timelineKey = item.id || item.createdAt || index;
              const costCurrency =
                item.details?.currency || reclamation.currency || reclamation.issueSite?.currency;
              const formatCostValue = (value) =>
                value === null || value === undefined || value === ''
                  ? ''
                  : formatAmount(value, costCurrency);
              const claimCreatedBy =
                item.eventType === 'claim_created' && (claimEmployeeName || item.creator)
                  ? claimEmployeeName || formatTimelineCreator(item.creator)
                  : '';
              return (
                <div key={timelineKey} className="drawer-timeline-item">
                  <div className="drawer-timeline-marker" />
                  <div className="drawer-timeline-content">
                    <div className="drawer-timeline-date">
                      {item.createdAt ? moment(item.createdAt).format('MMM D, YYYY h:mm A') : ''}
                      {item.creator && <span> {formatTimelineCreator(item.creator)}</span>}
                    </div>
                    <div className="drawer-timeline-title">
                      {claimCreatedBy
                        ? `Claim created by ${claimCreatedBy}`
                        : getTimelineTitle(
                            item,
                            formatCostValue,
                            TIMELINE_LABELS,
                            formatStatusName
                          )}
                    </div>
                    {item.eventType === 'call_logged' &&
                      (item.details?.note || item.details?.outcome) && (
                        <div className="drawer-timeline-note">
                          {item.details?.outcome && (
                            <div className="drawer-timeline-outcome">
                              Outcome: {formatCallOutcome(item.details.outcome)}
                            </div>
                          )}
                          {item.details?.note && (
                            <div>
                              {getTimelineNotePreview(
                                item.details.note,
                                expandedTimelineNotes[timelineKey],
                                timelineNoteMax
                              )}
                              {shouldShowTimelineNoteToggle(item.details.note, timelineNoteMax) && (
                                <button
                                  type="button"
                                  className="drawer-timeline-note-toggle"
                                  onClick={() =>
                                    setExpandedTimelineNotes((prev) => ({
                                      ...prev,
                                      [timelineKey]: !prev[timelineKey],
                                    }))
                                  }
                                >
                                  {expandedTimelineNotes[timelineKey] ? 'Show Less' : 'Show More'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
          {timelineItems.length > 5 && (
            <button
              type="button"
              className="button inverted drawer-timeline-toggle"
              onClick={() => setShowAllTimeline((prev) => !prev)}
            >
              {showAllTimeline ? 'Show Less' : 'Show More'}
            </button>
          )}
        </div>
      )}

      {/* Claim Details */}
      <div className="drawer-section">
        <h4 className="drawer-section-title">Claim Details</h4>
        <div className="drawer-grid">
          {reclamation.issueDate && (
            <div className="drawer-field">
              <strong>Incident Date:</strong>{' '}
              {moment(reclamation.issueDate).format('MM/DD/YYYY h:mm A')}
            </div>
          )}
          {reclamation.issueSite?.name && (
            <div className="drawer-field">
              <strong>Issue Site:</strong> {reclamation.issueSite.name}
            </div>
          )}
          {reclamation.issueWashType !== undefined && (
            <div className="drawer-field">
              <strong>Wash Type:</strong>{' '}
              {reclamation.issueWashType === 0
                ? 'Exterior'
                : reclamation.issueWashType === 1
                ? 'Interior'
                : 'Both'}
            </div>
          )}
          {reclamation.description && (
            <div className="drawer-field">
              <strong>Description of Incident:</strong> {reclamation.description}
            </div>
          )}
        </div>
      </div>

      {shouldShowClaimChecklist && (
        <div className="drawer-section">
          <h4 className="drawer-section-title">Claim Checklist</h4>
          <div className="drawer-grid">
            {involvedLabel && (
              <div className="drawer-field">
                <strong>What was involved:</strong> {involvedLabel}
              </div>
            )}
            {claimChecklist.eventDate && (
              <div className="drawer-field">
                <strong>Date of Event:</strong>{' '}
                {moment(claimChecklist.eventDate).format('MM/DD/YYYY')}
              </div>
            )}
            {claimChecklist.eventTime && (
              <div className="drawer-field">
                <strong>Approx. Time:</strong>{' '}
                {moment(claimChecklist.eventTime, 'HH:mm').format('h:mm A')}
              </div>
            )}
            {formatYesNo(claimChecklist.anotherPartyInvolved) && (
              <div className="drawer-field">
                <strong>Another Party Involved:</strong>{' '}
                {formatYesNo(claimChecklist.anotherPartyInvolved)}
              </div>
            )}
            {formatYesNo(claimChecklist.policeReportFiled) && (
              <div className="drawer-field">
                <strong>Police Report Filed:</strong>{' '}
                {formatYesNo(claimChecklist.policeReportFiled)}
              </div>
            )}
            {formatYesNo(claimChecklist.injuries) && (
              <div className="drawer-field">
                <strong>Injuries:</strong> {formatYesNo(claimChecklist.injuries)}
              </div>
            )}
          </div>
          {(additionalContact.name || additionalContact.contact) && (
            <div className="drawer-subsection">
              <div className="drawer-field">
                <strong>Additional Contact:</strong>{' '}
                {[additionalContact.name, additionalContact.contact].filter(Boolean).join(' - ')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Damage Information (Claims only) */}
      {shouldShowClaimDamageInfo && (
        <div className="drawer-section">
          <h4 className="drawer-section-title">Damage Information</h4>
          {claimEventLabel && (
            <div className="drawer-field">
              <strong>Event:</strong> {claimEventLabel}
            </div>
          )}
          <div className="drawer-grid">
            {reclamation.incidentDetails?.damageInformation?.preExistingCondition && (
              <div className="drawer-field">✓ Evidence of pre-existing condition</div>
            )}
            {reclamation.incidentDetails?.damageInformation?.customerAdvicedBefore && (
              <div className="drawer-field">✓ Customer advised before wash</div>
            )}
            {reclamation.incidentDetails?.damageInformation?.itemNotFound && (
              <div className="drawer-field">✓ Item not found</div>
            )}
            {reclamation.incidentDetails?.damageInformation?.afterMarketItem && (
              <div className="drawer-field">✓ After market non-factory item</div>
            )}
            {reclamation.incidentDetails?.damageInformation?.oldVehicle && (
              <div className="drawer-field">✓ Old vehicle</div>
            )}
          </div>
          {reclamation.claimDamagePartId && (
            <div className="drawer-subsection">
              <div className="drawer-field">
                <strong>Car Part:</strong>{' '}
                {damageClaimOptions?.find(
                  (p) => Number(p.id) === Number(reclamation.claimDamagePartId)
                )?.value || reclamation.claimDamagePartId}
              </div>
              {reclamation.claimDamageAreaId && (
                <div className="drawer-subsection-item">
                  <strong>Affected Area:</strong>{' '}
                  {(() => {
                    const part = damageClaimOptions?.find(
                      (p) => Number(p.id) === Number(reclamation.claimDamagePartId)
                    );
                    const area = part?.options?.find(
                      (a) => Number(a.id) === Number(reclamation.claimDamageAreaId)
                    );
                    return area?.value || reclamation.claimDamageAreaId;
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Rewash Causes (Rewashes only) */}
      {reclamation.type === ReclamationType.Rewash && reclamation.incidentDetails?.needsRewash && (
        <div className="drawer-section">
          <h4 className="drawer-section-title">Rewash Causes</h4>
          <div className="drawer-grid">
            {reclamation.incidentDetails.needsRewash.dirtyWheels && (
              <div className="drawer-field">✓ Dirty wheels</div>
            )}
            {reclamation.incidentDetails.needsRewash.dirtyRockerPanel && (
              <div className="drawer-field">✓ Dirty rocker panel</div>
            )}
            {reclamation.incidentDetails.needsRewash.dirtyBackend && (
              <div className="drawer-field">✓ Dirty backend</div>
            )}
            {reclamation.incidentDetails.needsRewash.serviceFailure && (
              <div className="drawer-field">✓ Service failure</div>
            )}
            {reclamation.incidentDetails.needsRewash.missedSpots && (
              <div className="drawer-field">✓ Missed spots</div>
            )}
            {reclamation.incidentDetails.needsRewash.bugs && (
              <div className="drawer-field">✓ Bugs</div>
            )}
            {reclamation.incidentDetails.needsRewash.other && (
              <div className="drawer-field">✓ Other</div>
            )}
          </div>
        </div>
      )}

      {/* Tabs Section */}
      <div ref={mediaSectionRef}>
        <ContentTabs
          components={{
            Photos: (
              <Photos
                enableCameraCapture
                multipleFiles
                photos={
                  reclamation.files
                    ? reclamation.files.filter((f) => /image/.test(f.mimeType) && !f.archivedAt)
                    : []
                }
                addFile={(formData) => {
                  addFile({
                    siteId: reclamation.siteId,
                    reclamationId: reclamation.id,
                    file: formData,
                  });
                }}
                deleteFile={(file) => {
                  showDialog('CONFIRM_DIALOG', {
                    onReady: () => {
                      deleteFile({
                        fileId: file.id,
                        reclamationId: reclamation.id,
                      });
                    },
                    text: intl.formatMessage(
                      { id: 'confirmFileArchive' },
                      { fileName: file.originalName }
                    ),
                    title: intl.formatMessage({ id: 'archiveFile' }),
                  });
                }}
              />
            ),
            Videos: (
              <Videos
                videos={
                  reclamation.files
                    ? reclamation.files.filter((f) => /video/.test(f.mimeType) && !f.archivedAt)
                    : []
                }
                addFile={(formData) => {
                  addFile({
                    siteId: reclamation.siteId,
                    reclamationId: reclamation.id,
                    file: formData,
                  });
                }}
                deleteFile={(file) => {
                  showDialog('CONFIRM_DIALOG', {
                    onReady: () => {
                      deleteFile({
                        fileId: file.id,
                        reclamationId: reclamation.id,
                      });
                    },
                    text: intl.formatMessage(
                      { id: 'confirmFileArchive' },
                      { fileName: file.originalName }
                    ),
                    title: intl.formatMessage({ id: 'archiveFile' }),
                  });
                }}
              />
            ),
            Documents: (
              <Files
                files={
                  reclamation.files
                    ? reclamation.files.filter(
                        (f) => f.mimeType === 'application/pdf' && !f.archivedAt
                      )
                    : []
                }
                fileTypes={['document']}
                canAdd
                canDelete
                addFileFunction={(formData) => {
                  addFile({
                    siteId: reclamation.siteId,
                    reclamationId: reclamation.id,
                    file: formData,
                  });
                }}
                deleteFileFunction={(file) => {
                  showDialog('CONFIRM_DIALOG', {
                    onReady: () => {
                      deleteFile({
                        fileId: file.id,
                        reclamationId: reclamation.id,
                      });
                    },
                    text: intl.formatMessage(
                      { id: 'confirmFileArchive' },
                      { fileName: file.originalName }
                    ),
                    title: intl.formatMessage({ id: 'archiveFile' }),
                  });
                }}
              />
            ),
            Repair: <Repair reclamation={reclamation} />,
          }}
          scrollable={true}
        />
      </div>
    </div>
  );
};

export default ClaimDrawerContent;
